import React, { useState } from 'react';
import { Check } from 'lucide-react';
import './Pricing.css';
import { apiService } from '../services/api';
import Header from './Header';
import authBackground from '../assets/auth-background.jpg';

interface PricingProps {
  onNavigate?: (path: string) => void;
}

interface Plan {
  id: string;
  name: string;
  price: number | null;
  description: string;
  features: string[];
  cta: string;
  popular: boolean;
  priceId?: string;
  isContactUs?: boolean;
}

const Pricing: React.FC<PricingProps> = ({ onNavigate }) => {
  const [loading, setLoading] = useState<string | null>(null);

  const plans: Plan[] = [
    {
      id: 'free',
      name: 'Free',
      price: 0,
      description: 'Get started with basic features',
      features: [
        'Text Remi via iMessage',
        'Set reminders',
        'Take notes about people',
        'LinkedIn insights',
      ],
      cta: 'Get Started Free',
      popular: false,
    },
    {
      id: 'standard',
      name: 'Standard',
      price: 20,
      description: 'Everything you need to manage your network',
      features: [
        'All Free plan features',
        'Full online platform access',
        'CSV import contacts',
        '14-day trial',
      ],
      cta: 'Start Free Trial',
      popular: true,
      priceId: process.env.REACT_APP_STRIPE_PRICE_STANDARD,
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: null,
      description: 'For organizations',
      features: [
      ],
      cta: 'Contact Us',
      popular: false,
      isContactUs: true,
    },
  ];

  const handleSelectPlan = async (planId: string, priceId?: string, isContactUs?: boolean) => {
    // Handle Free plan - redirect to signup
    if (planId === 'free') {
      if (onNavigate) {
        onNavigate('/register');
      } else {
        window.location.href = '/register';
      }
      return;
    }

    // Handle Standard plan - redirect to signup (not authenticated users on public pricing page)
    if (planId === 'standard') {
      if (onNavigate) {
        onNavigate('/register');
      } else {
        window.location.href = '/register';
      }
      return;
    }

    // Handle "Contact Us" for Enterprise - open Calendly
    if (isContactUs || planId === 'enterprise') {
      window.open('https://calendly.com/rupa-itsremi/30min', '_blank');
      return;
    }
  };

  return (
    <>
      <Header onNavigate={onNavigate} />
      <div
        style={{
          backgroundImage: `url(${authBackground})`,
          backgroundPosition: 'center bottom',
          backgroundSize: 'cover',
          minHeight: '100vh',
          width: '100%'
        }}
      >
        <div className="pricing-container" style={{ paddingTop: '120px' }}>
          <div className="pricing-header">
            <h1>Choose Your Plan</h1>
            <p>Start with a 14-day free trial. Credit card required - cancel anytime.</p>
          </div>

          <div className="pricing-cards">
        {plans.map((plan) => (
          <div
            key={plan.id}
            className={`pricing-card ${plan.popular ? 'popular' : ''}`}
          >
            {plan.popular && <div className="popular-badge">Most Popular</div>}
            
            <div className="plan-header">
              <h2>{plan.name}</h2>
              <p className="plan-description">{plan.description}</p>
              <div className="price">
                {plan.price !== null ? (
                  <>
                    <span className="currency">$</span>
                    <span className="amount">{plan.price}</span>
                    <span className="period"> USD/month</span>
                  </>
                ) : (
                  <span className="amount" style={{ fontSize: '2rem' }}>Custom Pricing</span>
                )}
              </div>
              {plan.price !== null && plan.price > 0 && (
                <p className="trial-text">14-day free trial included</p>
              )}
            </div>

            <ul className="features-list">
              {plan.features.map((feature, index) => (
                <li key={index}>
                  <Check size={20} className="check-icon" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <button
              className={`cta-button ${plan.popular ? 'primary' : 'secondary'}`}
              onClick={() => handleSelectPlan(plan.id, plan.priceId, plan.isContactUs)}
              disabled={loading === plan.id}
            >
              {loading === plan.id ? 'Loading...' : plan.cta}
            </button>
          </div>
        ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default Pricing;

